import React from "react";
import PropTypes from "prop-types";
// External components
import { Card, IconButton, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
// Components
import TaxesAndDiscountsEditor from "../../../TaxesAndDiscountsEditor/TaxesAndDiscountsEditor";
// Context
import { useModal } from "../../../../context";
// SCSS
import "./SummaryExpenses.scss";
import {
  getSubTotal,
  getTaxedAmount,
  getTotal,
  getValuesSummatory,
} from "../../../../utils/Operations";
import useCoinDetails from "../../../../hooks/useCoinDetails";

function SummaryExpenses({ productData, setInfoSummary }) {
  const { setModalOpen } = useModal();
  const { currentSymbolCoin, toExchangeRate } = useCoinDetails();

  const handleDelete = () => {
    setInfoSummary(prev => prev.filter(i => i.id !== productData.id));
  };

  const handleTaxesAndDiscountsUpdate = ({
    newTariff,
    newTax,
    updatedDiscountsList,
  }) => {
    const newTotalDiscountDecimal =
      getValuesSummatory(updatedDiscountsList) / 100;
    const newDiscountedAmount =
      productData.unitPrice * productData.quantity * newTotalDiscountDecimal;
    const newSubtotal =
      getSubTotal(productData.unitPrice, newTotalDiscountDecimal) *
      productData.quantity;
    const newTaxedAmount = getTaxedAmount({
      porcentajeTarifa: newTariff.Percent,
      subTotal: newSubtotal,
    });
    const newTotal = getTotal(newSubtotal, newTaxedAmount);
    setInfoSummary(products =>
      products.map(product =>
        product.id === productData.id
          ? {
              ...product,
              tariff: newTariff || product.tariff,
              tax: newTax || product.tax,
              discountPercentageList: updatedDiscountsList,
              tariffAmount: newTaxedAmount,
              disAmount: newDiscountedAmount,
              subTotal: newSubtotal,
              total: newTotal,
            }
          : product,
      ),
    );
  };

  return (
    <Card
      color="tertiaryAcento"
      sx={{
        maxHeight: "194x",
        width: "333px",
        minWidth: "333px",
        display: "flex",
        flexDirection: "column",
        padding: "1rem",
        gap: "0.1rem",
      }}
      variant="outlined"
    >
      <div className="content-grid-fixed-column">
        <Typography
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          typography="modeColor"
          variant="body1"
        >
          <b>Precio unitario</b>
          {currentSymbolCoin}
          {toExchangeRate(productData.unitPrice).toFixed(3)}
        </Typography>
        <Typography
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          typography="modeColor"
          variant="body1"
        >
          <b>Cantidad</b>
          {`${productData.quantity} ${productData.measurmentUnit.Symbol}`}
        </Typography>
      </div>
      <div className="content-grid-fixed-column">
        <Typography
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          typography="modeColor"
          variant="body1"
          width="100%"
        >
          <b>Descuento Total</b>
          {currentSymbolCoin}
          {toExchangeRate(productData.discountedAmount).toFixed(3)}
        </Typography>
        <Typography
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          typography="modeColor"
          variant="body1"
          width="100%"
        >
          <b>IVA</b>
          {currentSymbolCoin}
          {toExchangeRate(productData.taxedAmount).toFixed(3)}
        </Typography>
      </div>

      <div className="content-grid-fixed-column">
        <Typography
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          typography="modeColor"
          variant="body1"
          width="100%"
        >
          <b>SubTotal</b>
          {currentSymbolCoin}
          {toExchangeRate(productData.subTotal).toFixed(3)}
        </Typography>
        <Typography
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          typography="modeColor"
          variant="body1"
          width="100%"
        >
          <b>Total</b>
          {currentSymbolCoin}
          {toExchangeRate(productData.total).toFixed(3)}
        </Typography>
      </div>

      <div className="content-flex-end">
        <IconButton
          color="default"
          size="small"
          sx={{
            "backgroundColor": "transparent",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          onClick={() =>
            setModalOpen(
              true,
              <TaxesAndDiscountsEditor
                defaultValues={{
                  selectedTariffId: productData.tariffData.id,
                  selectedTaxId: productData.taxData.id,
                  totalPrice: productData.subTotal * productData.quantity,
                  percentageDiscountsList: productData.percentageDiscountsList,
                }}
                onCancel={() => setModalOpen(false)}
                onSubmit={({
                  selectedTariff,
                  selectedTax,
                  discountsList,
                  ...others
                }) => {
                  handleTaxesAndDiscountsUpdate({
                    newTariff: selectedTariff,
                    newTax: selectedTax,
                    updatedDiscountsList: discountsList,
                  });
                  setModalOpen(false);
                }}
              />,
            )
          }
        >
          <FontAwesomeIcon icon={faPenToSquare} />
        </IconButton>
        <IconButton
          color="default"
          size="small"
          sx={{
            "backgroundColor": "transparent",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          onClick={handleDelete}
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </IconButton>
      </div>
    </Card>
  );
}

SummaryExpenses.propTypes = {
  productData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    taxedAmount: PropTypes.number.isRequired,
    tariffData: PropTypes.object.isRequired,
    taxData: PropTypes.object.isRequired,
    quantity: PropTypes.number.isRequired,
    measurmentUnit: PropTypes.object.isRequired,
    percentageDiscountsList: PropTypes.array.isRequired,
    discountedAmount: PropTypes.number.isRequired,
    unitPrice: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    subTotal: PropTypes.number.isRequired,
  }).isRequired,
  setInfoSummary: PropTypes.func,
};

SummaryExpenses.defaultProps = {
  setInfoSummary: e => e,
};

export default SummaryExpenses;
